/* ------------------------------------------------------------------
   Search modal — open/close + scroll-lock + autofocus + Esc + click-out
   + predictive search (debounced /search/suggest.json fetch).
   Reference: philps-site/components/sections/Header.tsx (searchOpen).
   ------------------------------------------------------------------ */

(function () {
  const modal = document.querySelector('[data-search-modal]');
  if (!modal) return;

  const scrim = modal.querySelector('[data-search-scrim]');
  const input = modal.querySelector('[data-search-input]');
  const results = modal.querySelector('[data-search-results]');
  const form = modal.querySelector('.search-2026__form');

  // ---- Open / close --------------------------------------------------
  function open() {
    if (modal.classList.contains('is-open')) return;
    modal.classList.add('is-open');
    modal.setAttribute('aria-hidden', 'false');
    document.body.classList.add('search-2026-open');
    if (window.lenis && typeof window.lenis.stop === 'function') {
      window.lenis.stop();
    }
    requestAnimationFrame(function () {
      if (input) input.focus({ preventScroll: true });
    });
  }

  function close() {
    if (!modal.classList.contains('is-open')) return;
    modal.classList.remove('is-open');
    modal.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('search-2026-open');
    if (window.lenis && typeof window.lenis.start === 'function') {
      window.lenis.start();
    }
    if (input) input.value = '';
    clearResults();
  }

  // ---- Predictive search ---------------------------------------------
  const PREDICT_URL = '/search/suggest.json';
  const PREDICT_PARAMS =
    '&resources[type]=product,query,collection,article,page' +
    '&resources[limit]=6' +
    '&resources[options][unavailable_products]=last';

  let inflight = null;
  let debounceTimer = null;

  function clearResults() {
    if (!results) return;
    results.innerHTML = '';
    results.hidden = true;
  }

  function formatMoney(cents) {
    // Shopify returns price as a string (e.g. "12.95") or sometimes a
    // pre-formatted string already. Try Intl.NumberFormat for clean
    // output; fall back to the raw value.
    if (cents == null) return '';
    var n = parseFloat(cents);
    if (isNaN(n)) return String(cents);
    try {
      return new Intl.NumberFormat('en-GB', {
        style: 'currency',
        currency: (window.Shopify && Shopify.currency && Shopify.currency.active) || 'GBP',
      }).format(n);
    } catch (e) {
      return '£' + n.toFixed(2);
    }
  }

  function escapeHtml(str) {
    return String(str || '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  function productImage(product) {
    // Predictive search exposes featured_image in older shops and
    // image in newer ones; check both.
    if (product.featured_image && product.featured_image.url) return product.featured_image.url;
    if (product.image) return product.image;
    return '';
  }

  function render(data, term) {
    if (!results) return;
    var sets = (data && data.resources && data.resources.results) || {};
    var products = sets.products || [];
    var queries = sets.queries || [];
    var collections = sets.collections || [];
    var pages = sets.pages || [];
    var articles = sets.articles || [];

    var hasAny =
      products.length || queries.length || collections.length || pages.length || articles.length;
    if (!hasAny) {
      results.innerHTML =
        '<p class="search-2026__results-empty">No matches for &ldquo;' +
        escapeHtml(term) + '&rdquo;.</p>';
      results.hidden = false;
      return;
    }

    var html = '';

    // Query suggestions — quick text-only completions, rendered as
    // chip-style links across the top.
    if (queries.length) {
      html += '<div class="search-2026__results-section search-2026__results-section--queries">';
      html += '<p class="search-2026__results-label">Suggestions</p>';
      html += '<ul class="search-2026__queries">';
      queries.slice(0, 4).forEach(function (q) {
        var text = q.text || q.styled_text || '';
        html += '<li><a class="search-2026__query" href="/search?q=' +
          encodeURIComponent(text) + '">' + escapeHtml(text) + '</a></li>';
      });
      html += '</ul></div>';
    }

    // Products
    if (products.length) {
      html += '<div class="search-2026__results-section">';
      html += '<p class="search-2026__results-label">Products</p>';
      html += '<ul class="search-2026__products">';
      products.slice(0, 6).forEach(function (p) {
        var img = productImage(p);
        var price = formatMoney(p.price);
        html += '<li><a class="search-2026__product" href="' + escapeHtml(p.url) + '">';
        html += '<span class="search-2026__product-image">';
        if (img) {
          html += '<img src="' + escapeHtml(img) + '" alt="" loading="lazy" width="56" height="56">';
        }
        html += '</span>';
        html += '<span class="search-2026__product-text">';
        html += '<span class="search-2026__product-title">' + escapeHtml(p.title) + '</span>';
        if (price) html += '<span class="search-2026__product-price">' + price + '</span>';
        html += '</span>';
        html += '</a></li>';
      });
      html += '</ul></div>';
    }

    // Collections
    if (collections.length) {
      html += '<div class="search-2026__results-section">';
      html += '<p class="search-2026__results-label">Collections</p>';
      html += '<ul class="search-2026__links">';
      collections.slice(0, 3).forEach(function (c) {
        html += '<li><a class="search-2026__link" href="' + escapeHtml(c.url) + '">' + escapeHtml(c.title) + '</a></li>';
      });
      html += '</ul></div>';
    }

    // Pages
    if (pages.length) {
      html += '<div class="search-2026__results-section">';
      html += '<p class="search-2026__results-label">Pages</p>';
      html += '<ul class="search-2026__links">';
      pages.slice(0, 3).forEach(function (pg) {
        html += '<li><a class="search-2026__link" href="' + escapeHtml(pg.url) + '">' + escapeHtml(pg.title) + '</a></li>';
      });
      html += '</ul></div>';
    }

    // Articles
    if (articles.length) {
      html += '<div class="search-2026__results-section">';
      html += '<p class="search-2026__results-label">Articles</p>';
      html += '<ul class="search-2026__links">';
      articles.slice(0, 3).forEach(function (a) {
        html += '<li><a class="search-2026__link" href="' + escapeHtml(a.url) + '">' + escapeHtml(a.title) + '</a></li>';
      });
      html += '</ul></div>';
    }

    // "See all results" footer link
    html += '<a class="search-2026__see-all" href="/search?q=' + encodeURIComponent(term) + '">' +
      'See all results for &ldquo;' + escapeHtml(term) + '&rdquo;' +
      '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true">' +
      '<path d="M5 12h14M13 6l6 6-6 6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>' +
      '</svg></a>';

    results.innerHTML = html;
    results.hidden = false;
  }

  function fetchSuggestions(term) {
    if (inflight && inflight.abort) {
      try { inflight.abort(); } catch (e) { /* ignore */ }
    }
    var controller = (typeof AbortController !== 'undefined') ? new AbortController() : null;
    inflight = controller;

    var url = PREDICT_URL + '?q=' + encodeURIComponent(term) + PREDICT_PARAMS;
    fetch(url, {
      credentials: 'same-origin',
      headers: { Accept: 'application/json' },
      signal: controller ? controller.signal : undefined,
    })
      .then(function (r) {
        if (!r.ok) throw new Error('Suggest fetch failed (' + r.status + ')');
        return r.json();
      })
      .then(function (data) { render(data, term); })
      .catch(function (err) {
        if (err && err.name === 'AbortError') return;
        console.error('[search-2026] suggest failed', err);
      });
  }

  function onInput() {
    var term = (input.value || '').trim();
    clearTimeout(debounceTimer);
    if (!term) {
      clearResults();
      return;
    }
    // Debounce to ~120ms — feels responsive without hammering Shopify.
    debounceTimer = setTimeout(function () { fetchSuggestions(term); }, 120);
  }

  if (input) {
    input.addEventListener('input', onInput);
    // If the modal is reopened with a value already present (rare —
    // we clear on close — but covers the case), kick off a fetch.
    input.addEventListener('focus', function () {
      if ((input.value || '').trim()) onInput();
    });
  }

  // Allow Enter to submit even when focus is in the results pane via
  // mouse hover — the form submit naturally posts to /search?q=...
  if (form) {
    form.addEventListener('submit', function () {
      // No need to clear results; the page navigates away.
    });
  }

  // ---- Triggers -----------------------------------------------------
  document.addEventListener('click', function (e) {
    const opener = e.target.closest('[data-search-open]');
    if (opener) {
      e.preventDefault();
      open();
    }
  });

  // ---- Close handlers -----------------------------------------------
  if (scrim) scrim.addEventListener('click', close);

  document.addEventListener('keydown', function (e) {
    if (e.key === 'Escape' && modal.classList.contains('is-open')) {
      close();
    }
  });

  // ---- Public API ---------------------------------------------------
  window.PhilpsSearch = { open: open, close: close };
})();
